import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  CheckCircle2,
  Truck,
  ShieldCheck,
  Phone,
  MapPin,
  Home,
  Package,
  Banknote,
  Send,
} from "lucide-react";
import { addOrder } from "@/lib/storage";
import { sendOrderPlacedNotification } from "@/lib/notifications";
import { silentSignup } from "@/lib/customerAuth";
import {
  CUSTOMER_CHANGE_EVENT,
  getCachedCustomer,
} from "@/lib/customerAuth";
import { CHECKOUT } from "@/constants/testIds";
import { LazyImage } from "./LazyImage";

const formatINR = (n) =>
  `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman & Nicobar Islands", "Chandigarh",
  "Dadra & Nagar Haveli and Daman & Diu", "Delhi",
  "Jammu & Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

const EMPTY_FORM = {
  name: "",
  phone: "",
  email: "",
  altPhone: "",
  pincode: "",
  state: "",
  city: "",
  house: "",
  colony: "",
};

// Accepts a `target` of the form { product } (Buy Now) or { items } (Cart).
export const CheckoutDialog = ({ target, open, onClose, onPlaced }) => {
  const isCart = Array.isArray(target?.items);
  const items = useMemo(() => {
    if (isCart) return target.items;
    if (target?.product) {
      const p = target.product;
      return [
        {
          ...p,
          qty: 1,
          size: p.selectedSize || p.size || "Free Size",
          color: p.selectedColor || null,
          colorLabel: p.selectedColorLabel || null,
        },
      ];
    }
    return [];
  }, [target, isCart]);

  const total = useMemo(
    () =>
      items.reduce(
        (acc, i) => acc + (Number(i.price) || 0) * (Number(i.qty) || 1),
        0,
      ),
    [items],
  );

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [prefilled, setPrefilled] = useState(false);

  // P3a — when a signed-in customer opens checkout, prefill the empty fields
  // from their cached profile. Only fills EMPTY fields, never overwrites
  // anything the user has already typed. Stops once the form has been touched
  // so re-opens don't clobber edits during the same session.
  useEffect(() => {
    if (!open || prefilled) return;
    const customer = getCachedCustomer();
    if (!customer) return;
    const phone =
      typeof customer.phone === "string"
        ? customer.phone.replace(/\D+/g, "").slice(-10)
        : "";
    setForm((p) => {
      const next = { ...p };
      if (!next.name && customer.name) next.name = customer.name;
      if (!next.phone && phone) next.phone = phone;
      return next;
    });
    setPrefilled(true);
  }, [open, prefilled]);

  // Reset the prefill latch when the dialog closes so the next open re-fills
  // fresh customer data.
  useEffect(() => {
    if (!open) setPrefilled(false);
  }, [open]);

  // If the customer signs in/out WHILE the dialog is open, refresh once.
  useEffect(() => {
    if (!open) return;
    const onChange = () => {
      const c = getCachedCustomer();
      if (!c) return;
      const phone = typeof c.phone === "string" ? c.phone.replace(/\D+/g, "").slice(-10) : "";
      setForm((p) => ({
        ...p,
        name: p.name || c.name || "",
        phone: p.phone || phone,
      }));
    };
    window.addEventListener(CUSTOMER_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CUSTOMER_CHANGE_EVENT, onChange);
  }, [open]);

  const update = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2)
      e.name = "Enter your full name";
    if (!/^[6-9]\d{9}$/.test(form.phone.trim()))
      e.phone = "Enter a valid 10-digit Indian mobile";
    if (form.altPhone.trim() && !/^[6-9]\d{9}$/.test(form.altPhone.trim()))
      e.altPhone = "Alt mobile is invalid";
    if (!/^\d{6}$/.test(form.pincode.trim()))
      e.pincode = "6-digit pincode";
    if (!form.state) e.state = "Select your state";
    if (!form.city.trim() || form.city.trim().length < 2)
      e.city = "Enter your city";
    if (!form.house.trim() || form.house.trim().length < 2)
      e.house = "House / Building required";
    if (!form.colony.trim() || form.colony.trim().length < 2)
      e.colony = "Colony / Area required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (submitting || success) return;
    if (!validate()) return;

    setSubmitting(true);
    try {
      const order = await addOrder(
        isCart
          ? { items, ...form }
          : { product: items[0], ...form },
      );
      // Silent auto-signup: creates a customer account behind the scenes so
      // the browser remembers them for next visit. No login screen shown.
      await silentSignup({
        email: form.email,
        phone: form.phone,
        name: form.name,
      });
      const notify = await sendOrderPlacedNotification({
        phone: form.phone,
        order,
      });
      setSuccess({ order, notify });
      onPlaced?.({ wasCart: isCart });
    } catch (err) {
      // Surface the failure so the user can retry instead of being stuck.
      setErrors((p) => ({
        ...p,
        _root: err?.message || "Could not place order. Please try again.",
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setSuccess(null);
    setForm(EMPTY_FORM);
    setErrors({});
    onClose();
  };

  return (
    <AnimatePresence>
      {open && items.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-6 bg-black/55 backdrop-blur-sm"
          onClick={close}
          data-testid={CHECKOUT.modal}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 24 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-3xl bg-white sm:rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.22)] overflow-hidden max-h-[100vh] sm:max-h-[92vh] flex flex-col"
          >
            {/* Gold hairline */}
            <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[#E0B53C]/70 to-transparent" />

            <button
              onClick={close}
              data-testid={CHECKOUT.closeBtn}
              aria-label="close"
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/95 hover:bg-neutral-100 flex items-center justify-center shadow-sm border border-black/5 transition-colors"
            >
              <X size={16} strokeWidth={1.8} />
            </button>

            {success ? (
              <SuccessView
                order={success.order}
                notify={success.notify}
                items={items}
                onClose={close}
              />
            ) : (
              <CheckoutForm
                items={items}
                total={total}
                form={form}
                update={update}
                errors={errors}
                submitting={submitting}
                onSubmit={submit}
              />
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const CheckoutForm = ({
  items,
  total,
  form,
  update,
  errors,
  submitting,
  onSubmit,
}) => (
  <form
    onSubmit={onSubmit}
    className="overflow-y-auto"
    data-testid="checkout-form"
  >
    <header className="px-6 sm:px-8 pt-6 sm:pt-7 pb-4 border-b border-black/5">
      <p className="text-[11px] uppercase tracking-[0.24em] text-[#064E3B] font-semibold">
        Secure checkout · MastiBazaar
      </p>
      <h2 className="font-display text-3xl sm:text-4xl text-[#0B0C10] mt-1 font-light tracking-tight">
        Confirm your order.
      </h2>
    </header>

    <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
      {/* LEFT — form */}
      <div className="lg:col-span-3 p-6 sm:p-8 space-y-7">
        {/* Contact */}
        <section className="space-y-3">
          <SectionTitle icon={<Phone size={14} strokeWidth={1.8} />}>
            Contact details
          </SectionTitle>
          <Field
            label="Full Name"
            testId={CHECKOUT.nameInput}
            value={form.name}
            onChange={update("name")}
            error={errors.name}
            placeholder="Aisha Kapoor"
          />
          <Field
            label="Email (Gmail) — for order updates"
            testId="checkout-email-input"
            value={form.email}
            onChange={update("email")}
            error={errors.email}
            placeholder="you@gmail.com"
            inputMode="email"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field
              label="Mobile Number"
              testId={CHECKOUT.phoneInput}
              value={form.phone}
              onChange={update("phone")}
              error={errors.phone}
              placeholder="9876543210"
              inputMode="numeric"
              maxLength={10}
              prefix="+91"
            />
            <Field
              label="Alternative Mobile (optional)"
              testId="checkout-altphone-input"
              value={form.altPhone}
              onChange={update("altPhone")}
              error={errors.altPhone}
              placeholder="9876543210"
              inputMode="numeric"
              maxLength={10}
              prefix="+91"
            />
          </div>
        </section>

        {/* Address */}
        <section className="space-y-3">
          <SectionTitle icon={<MapPin size={14} strokeWidth={1.8} />}>
            Delivery address
          </SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field
              label="Pincode"
              testId={CHECKOUT.pincodeInput}
              value={form.pincode}
              onChange={update("pincode")}
              error={errors.pincode}
              placeholder="110001"
              inputMode="numeric"
              maxLength={6}
            />
            <SelectField
              label="State"
              testId="checkout-state-input"
              value={form.state}
              onChange={update("state")}
              error={errors.state}
              options={INDIAN_STATES}
              placeholder="Select state"
            />
            <Field
              label="City"
              testId="checkout-city-input"
              value={form.city}
              onChange={update("city")}
              error={errors.city}
              placeholder="Mumbai"
              className="sm:col-span-2"
            />
            <Field
              label="House No. / Building"
              testId="checkout-house-input"
              value={form.house}
              onChange={update("house")}
              error={errors.house}
              placeholder="Flat 402, Marigold Heights"
              className="sm:col-span-2"
            />
            <Field
              label="Colony / Area / Street"
              testId="checkout-colony-input"
              value={form.colony}
              onChange={update("colony")}
              error={errors.colony}
              placeholder="24 Linking Road, Bandra West"
              className="sm:col-span-2"
            />
          </div>
          {/* Hidden legacy combined-address mirror for back-compat callers */}
          <input
            type="hidden"
            data-testid={CHECKOUT.addressInput}
            value={[form.house, form.colony, form.city, form.state, form.pincode]
              .filter(Boolean)
              .join(", ")}
            readOnly
          />
        </section>

        {/* Payment — locked to COD */}
        <section className="space-y-3">
          <SectionTitle icon={<Banknote size={14} strokeWidth={1.8} />}>
            Payment method
          </SectionTitle>
          <div
            data-testid="checkout-payment-cod"
            className="rounded-2xl border-2 border-[#022C22] bg-[#022C22] text-white p-4 flex items-center gap-4"
          >
            <div className="w-10 h-10 rounded-full bg-[#E0B53C]/15 flex items-center justify-center">
              <Truck size={18} strokeWidth={1.8} className="text-[#E0B53C]" />
            </div>
            <div className="flex-1">
              <p className="font-display text-base text-white">
                Cash on Delivery
              </p>
              <p className="text-xs text-white/70">
                Pay in cash when your order arrives. No online payment needed.
              </p>
            </div>
            <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#E0B53C] border border-[#E0B53C]/40 rounded-full px-2.5 py-1">
              Default
            </span>
          </div>
        </section>
      </div>

      {/* RIGHT — order summary */}
      <aside className="lg:col-span-2 bg-neutral-50 p-6 sm:p-8 lg:border-l border-black/5 space-y-5">
        <SectionTitle icon={<Package size={14} strokeWidth={1.8} />}>
          Order summary
        </SectionTitle>

        <ul className="space-y-3 max-h-72 overflow-y-auto pr-1">
          {items.map((it, idx) => (
            <li
              key={idx}
              className="flex items-center gap-3"
              data-testid={`checkout-summary-item-${idx}`}
            >
              <div className="w-14 h-14 rounded-xl overflow-hidden bg-white shrink-0 relative ring-1 ring-black/5">
                <LazyImage
                  src={it.image}
                  alt={it.title}
                  wrapperClassName="w-full h-full"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-sm text-[#0B0C10] truncate">
                  {it.title}
                </p>
                <p className="text-[11px] text-neutral-600 uppercase tracking-[0.14em]">
                  Size {it.size || "Free Size"}
                  {it.colorLabel ? ` · ${it.colorLabel}` : ""} · ×{it.qty || 1}
                </p>
              </div>
              <p className="font-display text-sm font-medium shrink-0">
                {formatINR((it.price || 0) * (it.qty || 1))}
              </p>
            </li>
          ))}
        </ul>

        <div className="border-t border-black/5 pt-4 space-y-2 text-sm">
          <Row label="Subtotal" value={formatINR(total)} />
          <Row label="Shipping" value="FREE" muted />
          <div className="flex items-baseline justify-between pt-2 border-t border-black/5">
            <span className="text-xs uppercase tracking-[0.22em] text-neutral-600 font-semibold">
              Total
            </span>
            <span
              className="font-display text-3xl text-[#0B0C10]"
              data-testid="checkout-summary-total"
            >
              {formatINR(total)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 text-[11px] text-neutral-600 bg-white border border-black/5 rounded-xl p-3">
          <ShieldCheck
            size={14}
            strokeWidth={1.8}
            className="text-[#064E3B] shrink-0"
          />
          <span>
            Verified seller · 7-day no-questions return · Confirmation call
            before dispatch
          </span>
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid={CHECKOUT.submitBtn}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#0B0C10] text-white rounded-full py-3.5 font-medium tracking-wide hover:-translate-y-0.5 hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? (
            "Placing your order…"
          ) : (
            <>
              <Send size={15} strokeWidth={1.8} />
              Confirm COD Order · {formatINR(total)}
            </>
          )}
        </button>
        <p className="text-[10px] text-neutral-500 text-center uppercase tracking-[0.18em]">
          A confirmation SMS will be sent to your mobile.
        </p>
        <p
          data-testid="checkout-return-policy-line"
          className="text-[11px] text-[#064E3B] text-center font-medium mt-1"
        >
          5 Days Return Policy · Free returns on damaged / wrong items
        </p>
      </aside>
    </div>
  </form>
);

const SuccessView = ({ order, notify, items, onClose }) => (
  <div
    className="p-6 sm:p-10 overflow-y-auto"
    data-testid={CHECKOUT.successScreen}
  >
    <div className="text-center space-y-3 mb-7">
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 220, damping: 18 }}
        className="mx-auto w-20 h-20 rounded-full bg-[#D1FFEB] flex items-center justify-center ring-4 ring-[#D1FFEB]/40"
      >
        <CheckCircle2 size={36} strokeWidth={1.8} className="text-[#064E3B]" />
      </motion.div>
      <p className="text-[11px] uppercase tracking-[0.24em] text-[#064E3B] font-semibold">
        Order confirmed
      </p>
      <h2 className="font-display text-4xl text-[#0B0C10] font-light">
        Order placed.
      </h2>
      <p className="text-sm text-neutral-600">
        Order <span className="font-mono">#{order.id}</span> ·{" "}
        {new Date(order.createdAt).toLocaleString("en-IN", {
          dateStyle: "medium",
          timeStyle: "short",
        })}
      </p>
    </div>

    {/* SMS notification card */}
    <div
      className="rounded-2xl border border-[#E0B53C]/40 bg-[#022C22] text-white p-4 flex items-center gap-3 mb-5"
      data-testid="success-notification-card"
    >
      <div className="w-10 h-10 rounded-full bg-[#E0B53C]/20 flex items-center justify-center shrink-0">
        <Send size={16} strokeWidth={1.8} className="text-[#E0B53C]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#E0B53C] font-semibold">
          Confirmation sent
        </p>
        <p className="text-sm text-white truncate">
          {notify?.to || `+91${order.phone}`} · &ldquo;Order Successfully
          Placed! Confirm your order on MastiBazaar.in.&rdquo;
        </p>
      </div>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
      {/* Delivery address card */}
      <div
        className="rounded-2xl border border-black/5 bg-white p-5"
        data-testid="success-address-card"
      >
        <div className="flex items-center gap-2 mb-3">
          <Home size={14} strokeWidth={1.8} className="text-[#064E3B]" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">
            Delivering to
          </span>
        </div>
        <p className="font-display text-base text-[#0B0C10]">{order.name}</p>
        <p className="text-sm text-neutral-700">
          +91 {order.phone}
          {order.altPhone && (
            <span className="text-neutral-500"> · alt +91 {order.altPhone}</span>
          )}
        </p>
        <div className="text-sm text-neutral-700 mt-2 leading-relaxed">
          <p>{order.house}</p>
          <p>{order.colony}</p>
          <p>
            {order.city}, {order.state} — {order.pincode}
          </p>
        </div>
      </div>

      {/* Payment card */}
      <div
        className="rounded-2xl border border-black/5 bg-white p-5"
        data-testid="success-payment-card"
      >
        <div className="flex items-center gap-2 mb-3">
          <Banknote size={14} strokeWidth={1.8} className="text-[#064E3B]" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">
            Payment
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#022C22] text-[#E0B53C] text-xs font-semibold px-2.5 py-1 uppercase tracking-[0.14em]">
            <Truck size={12} strokeWidth={2} />
            COD
          </span>
          <span className="text-sm text-neutral-700">Cash on delivery</span>
        </div>
        <div className="border-t border-black/5 mt-4 pt-3 space-y-1.5 text-sm">
          <div className="flex justify-between text-neutral-700">
            <span>Subtotal</span>
            <span>{formatINR(order.totalPrice)}</span>
          </div>
          <div className="flex justify-between text-neutral-700">
            <span>Shipping</span>
            <span>FREE</span>
          </div>
          <div className="flex items-baseline justify-between pt-2 border-t border-black/5">
            <span className="text-xs uppercase tracking-[0.18em] font-semibold">
              Total
            </span>
            <span className="font-display text-2xl text-[#0B0C10]">
              {formatINR(order.totalPrice)}
            </span>
          </div>
        </div>
      </div>
    </div>

    {/* Items */}
    <div
      className="rounded-2xl border border-black/5 bg-white p-5 mb-5"
      data-testid="success-items-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Package size={14} strokeWidth={1.8} className="text-[#064E3B]" />
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">
          Items in this order ({order.totalQty})
        </span>
      </div>
      <ul className="space-y-3">
        {items.map((it, idx) => (
          <li key={idx} className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl overflow-hidden bg-white shrink-0 relative ring-1 ring-black/5">
              <LazyImage
                src={it.image}
                alt={it.title}
                wrapperClassName="w-full h-full"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display text-sm truncate">{it.title}</p>
              <p className="text-[11px] text-neutral-600 uppercase tracking-[0.14em]">
                Size {it.size}
                {it.colorLabel ? ` · ${it.colorLabel}` : ""} · ×{it.qty || 1}
              </p>
            </div>
            <p className="text-sm font-medium">
              {formatINR((it.price || 0) * (it.qty || 1))}
            </p>
          </li>
        ))}
      </ul>
    </div>

    <button
      onClick={onClose}
      data-testid="checkout-success-close"
      className="w-full bg-[#0B0C10] text-white rounded-full py-3.5 font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all"
    >
      Continue shopping
    </button>
  </div>
);

const SectionTitle = ({ icon, children }) => (
  <div className="flex items-center gap-2">
    <span className="text-[#064E3B]">{icon}</span>
    <span className="text-[11px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">
      {children}
    </span>
  </div>
);

const Row = ({ label, value, muted }) => (
  <div className="flex items-baseline justify-between">
    <span className="text-neutral-600">{label}</span>
    <span className={muted ? "text-neutral-500" : "text-[#0B0C10] font-medium"}>
      {value}
    </span>
  </div>
);

const Field = ({
  label,
  value,
  onChange,
  error,
  placeholder,
  testId,
  inputMode,
  maxLength,
  prefix,
  className = "",
}) => (
  <label className={`block ${className}`}>
    <span className="block text-[11px] uppercase tracking-[0.18em] text-neutral-600 mb-1.5 font-medium">
      {label}
    </span>
    <div
      className={`flex items-stretch overflow-hidden rounded-xl bg-white border ${error ? "border-red-400" : "border-black/10"} focus-within:border-[#0B0C10] focus-within:ring-1 focus-within:ring-[#0B0C10] transition-all`}
    >
      {prefix && (
        <span className="px-3 flex items-center text-sm text-neutral-500 bg-neutral-50 border-r border-black/5">
          {prefix}
        </span>
      )}
      <input
        data-testid={testId}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        inputMode={inputMode}
        maxLength={maxLength}
        className="flex-1 outline-none px-3 py-3 text-sm text-[#0B0C10] placeholder:text-neutral-400 bg-transparent"
      />
    </div>
    {error && (
      <span className="block text-[11px] text-red-500 mt-1">{error}</span>
    )}
  </label>
);

const SelectField = ({
  label,
  value,
  onChange,
  error,
  options,
  placeholder,
  testId,
  className = "",
}) => (
  <label className={`block ${className}`}>
    <span className="block text-[11px] uppercase tracking-[0.18em] text-neutral-600 mb-1.5 font-medium">
      {label}
    </span>
    <select
      data-testid={testId}
      value={value}
      onChange={onChange}
      className={`w-full bg-white border ${error ? "border-red-400" : "border-black/10"} focus:border-[#0B0C10] focus:ring-1 focus:ring-[#0B0C10] outline-none rounded-xl px-3 py-3 text-sm text-[#0B0C10] transition-all appearance-none`}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
    {error && (
      <span className="block text-[11px] text-red-500 mt-1">{error}</span>
    )}
  </label>
);
